/**
 * API base URL resolution for web + Capacitor native builds.
 *
 * - On the web (browser), paths are relative to the current origin, so
 *   `API_BASE_URL` is an empty string and `fetch('/api/...')` works as-is.
 * - On a Capacitor native build (Android / iOS), the app is served from
 *   a `capacitor://` or `https://localhost` origin that has no backend,
 *   so every API call must be sent absolute to the production server.
 *
 * The production host can be overridden at build time via the
 * `VITE_HORTON_API_URL` env var. If unset, it defaults to the public host.
 *
 * Usage:
 *   import { apiFetch, API_BASE_URL } from '@/config/api'
 *   const r = await apiFetch('/api/schedule/entries?start=2026-04-08')
 */

import { Capacitor } from '@capacitor/core'

const DEFAULT_NATIVE_API_URL = 'https://horton.io'

function resolveBaseUrl(): string {
  try {
    if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
      // @ts-ignore — import.meta.env is provided by Vite
      const envUrl: string | undefined = import.meta.env?.VITE_HORTON_API_URL
      return (envUrl || DEFAULT_NATIVE_API_URL).replace(/\/$/, '')
    }
  } catch {
    // Capacitor not loaded yet — fall through to relative
  }
  return ''
}

export const API_BASE_URL: string = resolveBaseUrl()

/** When true, roadblock CRUD uses `/api/roadblocks/v1` (richer detail + nested resources). */
export const ROADBLOCKS_V1_ENABLED: boolean =
  typeof import.meta !== 'undefined' &&
  (import.meta as { env?: { VITE_ROADBLOCKS_V1?: string } }).env?.VITE_ROADBLOCKS_V1 ===
    'true'

export const IS_NATIVE: boolean = (() => {
  try {
    return Boolean(Capacitor.isNativePlatform && Capacitor.isNativePlatform())
  } catch {
    return false
  }
})()

/**
 * Thin wrapper around fetch that prepends API_BASE_URL to relative paths
 * and always sends cookies (credentials: 'include'). Absolute URLs pass
 * through unchanged.
 */
export function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const url = input.startsWith('http://') || input.startsWith('https://')
    ? input
    : `${API_BASE_URL}${input.startsWith('/') ? '' : '/'}${input}`
  return fetch(url, {
    credentials: 'include',
    ...init,
  })
}

/** Convenience: build an absolute URL for contexts that need one (e.g. SSE). */
export function apiUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`
}
